import { ArrowRight, BookOpen, Lightbulb } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LoadingState } from '../components/ui/loading';
import { useInstantContent } from '../hooks/useStreamingContent';
import { getRelativeTime, getNewsUrl } from '../lib/content';

export function HomePage() {
  const { 
    posts, 
    examples,
    examplesLoading,
    hasInitialContent
  } = useInstantContent();

  // Get latest N posts for featured section - prioritize featured posts, then fill with latest
  const featuredPosts = posts
    .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime())
    .slice(0, 6);
  const featuredExamples = examples.slice(0, 3);

  return (
    <>
      <Helmet>
        <title>Promptliteracy.ai - Learn to talk to AI with clarity</title>
        <meta name="description" content="Daily news, proven prompt techniques, and clear explanations so anyone can use AI with confidence. Master AI literacy with our educational resources." />
        
        {/* Open Graph */}
        <meta property="og:title" content="Promptliteracy.ai - Learn to talk to AI with clarity" />
        <meta property="og:description" content="Daily news, proven prompt techniques, and clear explanations so anyone can use AI with confidence. Master AI literacy with our educational resources." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://promptliteracy.ai/" />
        <meta property="og:site_name" content="Promptliteracy.ai" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Promptliteracy.ai - Learn to talk to AI with clarity" />
        <meta name="twitter:description" content="Daily news, proven prompt techniques, and clear explanations so anyone can use AI with confidence." />
        
        {/* Additional SEO */}
        <meta name="keywords" content="AI literacy, prompt engineering, artificial intelligence education, AI communication, machine learning education, AI for beginners, prompt techniques" />
        <meta name="author" content="Promptliteracy.ai" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://promptliteracy.ai/" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            "name": "Promptliteracy.ai",
            "description": "Educational platform for AI literacy and prompt engineering skills",
            "url": "https://promptliteracy.ai/",
            "sameAs": ["https://twitter.com/@logikbug"],
            "educationalCredentialAwarded": "AI Literacy",
            "teaches": ["AI Communication", "Prompt Engineering", "AI Literacy"]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/15" />
        <div className="absolute top-20 right-20 w-32 h-32 bg-primary/30 rounded-full blur-3xl animate-pulse-tech" />
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-secondary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute top-1/3 left-1/4 w-24 h-24 bg-primary/15 rounded-full blur-2xl animate-float-gentle" />
        
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                Learn to talk to AI with{' '}
                <span className="gradient-text-tech">clarity</span>
              </h1>
              <div className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                <span className="relative inline-block mr-2 align-middle">
                  <img 
                    src="/logo-bulb.png" 
                    alt="PromptLiteracy.ai" 
                    className="h-7 w-5 animate-pulse-tech relative z-10"
                  />
                  <div className="absolute inset-0 -inset-1">
                    <div className="h-full w-full rounded-full bg-gradient-radial blur-lg"></div>
                  </div>
                </span>
                Our vision is a world where we don't just consume AI, but communicate with it clearly, shape it responsibly, and use it creatively.
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button size="lg" asChild className="text-lg px-8 py-3 btn-tech hover-lift">
                <Link to="/news">
                  Read Today's AI Literacy News
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-lg px-8 py-3 btn-tech border-primary/40 hover:border-primary hover:bg-primary/10">
                <Link to="/what-is-prompt-literacy">
                  What is Prompt Literacy?
                </Link>
              </Button>
            </div>

            {/* Quick stats */}
            <div className="flex flex-wrap justify-center gap-8 pt-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full" />
                <span>Daily curated news</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-secondary rounded-full" />
                <span>Proven techniques</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary/60 rounded-full" />
                <span>Clear explanations</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-16 bg-primary/5 relative overflow-hidden">
        <div className="absolute inset-0 circuit-pattern opacity-20" />
        <div className="container relative z-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold">Latest News</h2>
              <p className="text-muted-foreground mt-2">
                Stay informed about AI literacy developments
              </p>
            </div>
            <Button variant="outline" size="sm" asChild className="border-slate-300 btn-blue-hover transition-colors">
              <Link to="/news">
                View All News
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {!hasInitialContent ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <LoadingState key={i} message={`Loading news ${i.toString()}`} className="card-slate" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={getNewsUrl(post)}
                  className="block group"
                >
                  <Card className="card-slate hover-lift border-slate-300/30 cursor-pointer transition-all group-hover:border-primary/40 card-accent">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg leading-tight">
                          {post.title}
                        </CardTitle>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover-allowed flex-shrink-0 transition-colors" />
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{post.source_name}</span>
                        <span>•</span>
                        <span>{getRelativeTime(post.published_at)}</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        {post.dek}
                      </p>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3">
                        {post.topics.slice(0, 3).map((topic) => (
                          <Link 
                            key={topic}
                            to={`/tag/${topic}`}
                            className="inline-block px-2 py-0.5 text-xs bg-secondary/10 text-secondary hover:bg-secondary/20 hover:text-primary rounded transition-colors border border-secondary/20 whitespace-nowrap"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            {topic.replace('-', ' ')}
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Explainer */}
      <section className="py-16 bg-slate-50">
        <div className="container">
          <Card className="border-primary/20 bg-white shadow-lg card-accent">
            <CardContent className="p-8">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    <Badge variant="secondary">Featured Guide</Badge>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">
                    What is Prompt Literacy?
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Understand the fundamentals of communicating effectively with AI systems. 
                    Learn the core principles, common mistakes, and practical techniques that 
                    will make you a more effective AI user.
                  </p>
                  <Button asChild>
                    <Link to="/what-is-prompt-literacy">
                      Read the Guide
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                <div className="space-y-4">
                  <div className="bg-background/50 rounded-lg p-4 border border-primary/20">
                    <h4 className="font-semibold mb-2">Quick Start Checklist</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        Be specific about what you want
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        Provide relevant context
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        Set clear constraints
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        Iterate and refine
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        Verify results carefully
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Proven Techniques Preview */}
      <section className="py-16">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold">Proven Prompt Techniques</h2>
              <p className="text-muted-foreground mt-2">
                Real-world patterns that work
              </p>
            </div>
            <Button variant="outline" size="sm" asChild className="border-slate-300 btn-blue-hover transition-colors">
              <Link to="/examples">
                View All Examples
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {examplesLoading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="h-3 bg-muted rounded" />
                      <div className="h-3 bg-muted rounded w-4/5" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {featuredExamples.map((example) => (
                <Card key={example.slug} className="card-slate hover-lift border-slate-300/30 card-accent">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 hover:text-primary transition-colors">
                        {example.complexity}
                      </Badge>
                      <Badge variant="secondary" className="text-xs link-underline text-primary hover:text-primary transition-colors">
                        {example.audience}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">
                      {example.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      {example.context}
                    </p>
                    <Button variant="outline" size="sm" asChild className="w-full btn-tech">
                      <Link to={`/examples/${example.slug}`}>
                        View Example
                        <BookOpen className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Powered by Logikbug */}
      <section className="py-12 border-t bg-primary/3">
        <div className="container">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Promptliteracy.ai is a news and learning resource.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              For the full education platform, visit{' '}
              <a 
                href="https://www.logikbug.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Logikbug
              </a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}